/**
 * The contact card itself — a vCard, built to the spec rather than by joining
 * strings and hoping.
 *
 * ## Escaping is not optional
 *
 * vCard is a line-based format where `,` `;` `\` and newlines are structural.
 * An unescaped comma in "Bjarte Olsen, CCO" silently splits one field into two,
 * and the receiving phone shows a mangled contact — or drops the rest of the
 * card. Norwegian job titles carry commas routinely ("Salgssjef, Skida"), so
 * this is the common case rather than the exotic one.
 *
 * ## CRLF, not LF
 *
 * RFC 6350 §3.2 says lines end with CRLF. Most parsers tolerate bare LF; iOS
 * Contacts has historically been among the stricter ones, and this card exists
 * to be scanned by a stranger's iPhone at a trade fair. Costs one character.
 *
 * ## N as well as FN
 *
 * `FN` is the display name and `N` is the structured one. vCard 3.0 requires
 * both — a card with only `FN` imports on a phone and then sorts under nothing,
 * because the address book has no surname to file it by. Splitting a full name
 * into given and family is guesswork in general; here it is the last
 * whitespace-separated word, which is right for Norwegian names and wrong for
 * some others. Wrong-but-sortable beats absent.
 */

export interface CardIdentity {
  name: string
  title: string
  company: string
  phone: string
  email: string
  /** Optional link on the card. Not in the design; see `buildVcard`. */
  url?: string
}

/** Escape one property value. Order matters: backslash first, or it doubles. */
function esc(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n')
}

/** "Bjarte Skoglund Olsen" → `Olsen;Bjarte Skoglund;;;` */
function structuredName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ';;;;'
  const family = parts.length > 1 ? parts[parts.length - 1]! : ''
  const given = parts.length > 1 ? parts.slice(0, -1).join(' ') : parts[0]!
  return `${esc(family)};${esc(given)};;;`
}

export function buildVcard(id: CardIdentity): string {
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${structuredName(id.name)}`,
    `FN:${esc(id.name)}`,
  ]
  // Empty properties are omitted rather than sent blank: a `TEL:` with nothing
  // after it makes some address books create an empty phone number entry that
  // the recipient then has to delete by hand.
  if (id.title.trim()) lines.push(`TITLE:${esc(id.title)}`)
  if (id.company.trim()) lines.push(`ORG:${esc(id.company)}`)
  if (id.phone.trim()) lines.push(`TEL;TYPE=CELL,VOICE:${esc(id.phone)}`)
  if (id.email.trim()) lines.push(`EMAIL;TYPE=INTERNET,WORK:${esc(id.email)}`)
  if (id.url?.trim()) lines.push(`URL:${esc(id.url)}`)
  lines.push('END:VCARD')
  return lines.join('\r\n')
}

/** A filename a phone will accept, from a name that may contain anything. */
export function vcardFilename(name: string): string {
  const safe = name.trim().replace(/[^\p{L}\p{N}]+/gu, '_').replace(/^_+|_+$/g, '')
  return `${safe || 'contact'}.vcf`
}
